import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { markSectionItemComplete } from '@/lib/markSectionItemComplete';

interface XapiEvent {
  getVerb?: () => string;
  getVerifiedStatementValue?: (path: string[]) => unknown;
}

interface H5pInstance {
  contentId?: string | number;
  on?: (event: string, handler: (event: unknown) => void) => void;
  off?: (event: string, handler: (event: unknown) => void) => void;
}

export function useH5pCompletion(
  sectionItemId: string | undefined,
  enabled = true,
  courseId?: string,
  contentId?: string | number,
) {
  const markedRef = useRef(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled || !sectionItemId) return;

    markedRef.current = false;

    const markComplete = () => {
      if (markedRef.current) return;
      markedRef.current = true;
      void markSectionItemComplete(sectionItemId, { courseId, queryClient }).catch(() => {
        markedRef.current = false;
      });
    };

    const handleXapi = (event: unknown) => {
      const xapi = event as XapiEvent;
      if (markedRef.current || typeof xapi?.getVerb !== 'function') return;

      const verb = xapi.getVerb();
      const statesCompletion = xapi.getVerifiedStatementValue?.(['result', 'completion']) === true;
      if (verb !== 'completed' && verb !== 'answered' && !statesCompletion) return;

      const hasParent = xapi.getVerifiedStatementValue?.(['context', 'contextActivities', 'parent']);
      if (hasParent) return;

      markComplete();
    };

    const handleFinish = () => {
      markComplete();
    };

    const instanceBindings: Array<{ instance: H5pInstance; handler: (event: unknown) => void }> = [];
    let externalAttached = false;

    const attachToInstance = (instance: H5pInstance) => {
      if (!instance.on) return;
      instance.on('xAPI', handleXapi);
      instance.on('finish', handleFinish);
      instanceBindings.push({ instance, handler: handleXapi });
    };

    const attachToInstances = () => {
      for (const instance of (window.H5P?.instances ?? []) as H5pInstance[]) {
        if (
          contentId !== undefined &&
          String(instance.contentId) !== String(contentId)
        ) {
          continue;
        }
        if (instanceBindings.some((binding) => binding.instance === instance)) continue;
        attachToInstance(instance);
      }
    };

    const attachExternal = () => {
      if (!window.H5P?.externalDispatcher || externalAttached) return false;
      window.H5P.externalDispatcher.on('xAPI', handleXapi);
      externalAttached = true;
      return true;
    };

    attachExternal();
    attachToInstances();

    const timer = window.setInterval(() => {
      attachExternal();
      attachToInstances();
    }, 250);

    return () => {
      window.clearInterval(timer);
      if (externalAttached) {
        window.H5P?.externalDispatcher?.off?.('xAPI', handleXapi);
      }
      for (const binding of instanceBindings) {
        binding.instance.off?.('xAPI', binding.handler);
        binding.instance.off?.('finish', handleFinish);
      }
    };
  }, [contentId, courseId, enabled, queryClient, sectionItemId]);
}
